import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateInterviewMutation,
  useUpdateInterviewMutation,
} from "@/hooks/api/interview.queries";
import {
  InterviewType,
  InterviewTypeValue,
} from "@/services/interview.service";
import { toDateTimeLocalValue } from "@/utils/formatDate.util";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

interface InterviewDialogProps {
  applicationId: string;
  internshipId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Pass an existing interview to reschedule it. Rescheduling PATCHes the same
   * row — a fresh POST would create a second round and show the student two
   * interviews.
   */
  interview?: InterviewType | null;
  onSuccess?: () => void;
}

/** Which location field matters for each round. */
const needsMeetingLink = (type: string) =>
  type === "video" || type === "phone" || type === "technical" || type === "hr";
const needsVenue = (type: string) => type === "in_person";

const formSchema = z
  .object({
    interview_type: z.string().min(1, "Interview type is required"),
    scheduled_at: z.string().min(1, "Schedule time is required"),
    duration_minutes: z.string().min(1, "Duration is required"),
    meeting_link: z.string().optional(),
    venue: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (needsVenue(data.interview_type) && !data.venue?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Venue is required for an in-person interview",
        path: ["venue"],
      });
    }
    if (needsMeetingLink(data.interview_type) && !data.meeting_link?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Meeting link is required for this interview type",
        path: ["meeting_link"],
      });
    }
  });

type formType = z.infer<typeof formSchema>;

const interviewTypeOptions = [
  { label: "Phone", value: "phone" },
  { label: "Video", value: "video" },
  { label: "In Person", value: "in_person" },
  { label: "Technical", value: "technical" },
  { label: "HR", value: "hr" },
];

const emptyValues: formType = {
  interview_type: "technical",
  scheduled_at: "",
  duration_minutes: "30", // matches the API default
  meeting_link: "",
  venue: "",
};

const InterviewDialog = ({
  applicationId,
  internshipId,
  isOpen,
  onOpenChange,
  interview,
  onSuccess,
}: InterviewDialogProps) => {
  const isReschedule = !!interview;

  const createMutation = useCreateInterviewMutation(internshipId);
  const updateMutation = useUpdateInterviewMutation(internshipId, applicationId);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<formType>({
    resolver: zodResolver(formSchema),
    defaultValues: emptyValues,
  });

  const interviewType = form.watch("interview_type");
  const showMeetingLink = needsMeetingLink(interviewType);
  const showVenue = needsVenue(interviewType);

  useEffect(() => {
    if (!isOpen) return;
    form.reset(
      interview
        ? {
            interview_type: interview.interview_type,
            scheduled_at: toDateTimeLocalValue(interview.scheduled_at),
            duration_minutes: String(interview.duration_minutes ?? 30),
            meeting_link: interview.meeting_link ?? "",
            venue: interview.venue ?? "",
          }
        : emptyValues
    );
  }, [isOpen, interview, form]);

  const onSubmit = (data: formType) => {
    // `datetime-local` gives "YYYY-MM-DDTHH:mm" in local time; the API wants ISO-8601.
    const scheduledAt = new Date(data.scheduled_at).toISOString();
    const durationMinutes = Number(data.duration_minutes);

    // Only send the location field the chosen type actually uses, and clear the
    // other one so a switched type doesn't leave a stale link/venue behind.
    const meetingLink = showMeetingLink ? data.meeting_link?.trim() || "" : "";
    const venue = showVenue ? data.venue?.trim() || "" : "";

    const close = () => {
      onOpenChange(false);
      onSuccess?.();
    };

    if (interview) {
      updateMutation.mutate(
        {
          interviewId: interview.id,
          data: {
            scheduled_at: scheduledAt,
            duration_minutes: durationMinutes,
            meeting_link: meetingLink,
            venue,
          },
          successMessage: "Interview rescheduled",
          successDescription:
            "The student's copy was updated and a reschedule email was sent.",
        },
        { onSuccess: close }
      );
      return;
    }

    createMutation.mutate(
      {
        applicationId,
        data: {
          interview_type: data.interview_type as InterviewTypeValue,
          scheduled_at: scheduledAt,
          duration_minutes: durationMinutes,
          ...(meetingLink ? { meeting_link: meetingLink } : {}),
          ...(venue ? { venue } : {}),
        },
      },
      { onSuccess: close }
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) form.reset(emptyValues);
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isReschedule ? "Reschedule Interview" : "Schedule Interview"}
          </DialogTitle>
          <DialogDescription>
            {isReschedule
              ? "Update the time or location. The student's interview is updated in place and they receive a reschedule email."
              : "Set the interview details. The student is notified by email and sees this in their app."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <SelectField
                name="interview_type"
                label="Interview Type"
                options={interviewTypeOptions}
                required
                // The round type is fixed once created — schedule another round
                // instead of morphing an existing one.
                disabled={isReschedule}
              />
              <InputField
                name="duration_minutes"
                label="Duration (minutes)"
                type="number"
                min="15"
                step="15"
                required
              />
            </div>

            <InputField
              name="scheduled_at"
              label="Schedule At"
              type="datetime-local"
              required
            />

            {showMeetingLink && (
              <InputField
                name="meeting_link"
                label="Meeting Link"
                type="url"
                placeholder="https://meet.google.com/..."
                required
              />
            )}

            {showVenue && (
              <InputField
                name="venue"
                label="Venue"
                type="text"
                placeholder="e.g. Main Office, 3rd Floor"
                required
              />
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? isReschedule
                    ? "Saving..."
                    : "Scheduling..."
                  : isReschedule
                    ? "Save Changes"
                    : "Schedule"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewDialog;
